/**
 * Created by calhosh on 8/16/2024
 */
import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import envConfig from '@ey/voltron-config';
import MotifNav from '@ey/canvascoreservices/MotifNav';
import Utility from '../../../util/utilityfunctions';
import {labels} from '../../../util/utils';
import {useDocumentId, useEngagementId, useUserPreferences, useIsClientYearEndDateAfter} from '../../../util/customHooks';
import env from '../../../util/env';
import {documentTypes, EngagementStatus, independenceStatus, serviceURL, hideAIChatbotForEngagementSources} from '../../../util/uiconstants';
import RemoteComponent from '../ModuleFederation/RemoteComponent';
import NotificationPlaceholder from '../ModuleFederation/NotificationPlaceholder';
import TeamsInfo from '../TeamsUser/TeamsInfo';
import {MenuIconOptions} from './NavUtils';
import {getBookmarkAction} from '../../../actions/Task/getBookmarkActions';
import {raiseJSValidationError} from '../../../actions/erroractions';
import {getQuickLinks} from '../../../actions/Engagement/quicklinksactions';
import {getConfigurations} from '../../../actions/Configuration/configurationactions';
import {getTaskDocumentByDocumentId} from '../../../selectors/Ares/selectors';
import {getIsAutomationHubEnabled} from '../../../reducers/Configurations/configurationsreducer';
import ReactSelect from '../../Common/SelectDropdown/SelectDropdown';
import AIChatbot from '../ModuleFederation/AIChatbot';
import EngagementMap from '../../Common/EngagementMap/EngagementMap';
import MotifMenuOptions from './MotifMenuOptions';

export default function TopBarNav() {
	const isClientYearEndDateAfter = useIsClientYearEndDateAfter();
	const searchVariant = {
		control: 'select',
		options: ['expansible', 'overlap', 'fixed']
	};
	const [personData, setPersonData] = useState(undefined);
	const mode = Utility.getTheme();
	const engagementId = useEngagementId();
	const documentId = useDocumentId();
	const engagement = useSelector((store) => store.engagement);
	const internalUser = useSelector((store) => store.internalUser);
	const configurations = useSelector((store) => store.configurations);
	const quickLink = useSelector((store) => store.quickLink);
	const offlinePrep = useSelector((store) => store.offlinePrep);
	const formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const isProfileComplete = engagement?.isProfileComplete;
	const user = useUserPreferences();
	const dashboardDomain = env.getURL('hercules_web');
	const isAutomationHubEnabled = useSelector((store) => getIsAutomationHubEnabled(store));
	const zeusUrl = env.getURL('Zeus_web');
	const engagementMetaData = useSelector((store) => store.engagementMetaData);
	const [defaultSelectedEngagement, setDefaultSelectedEngagement] = useState(undefined);
	const [apiCallsComplete, setApiCallsComplete] = useState(false);
	const [iconOptions, setIconOptions] = useState([]);
	const [hideEverything, setHideEverything] = useState(true);
	const [engagementDropdownOptions, setEngagementDropdownOptions] = useState([]);
	const [gateWayOptionsOpen, setGateWayOptionsOpen] = useState(false);
	const [selectedIcon, setSelectedIcon] = useState(undefined);
	const [showAIChatbot, setShowAIChatbot] = useState(false);
	const [showChatbotIcon, setShowChatbotIcon] = useState(false);
	const [showEngagementMap, setShowEngagementMap] = useState(false);
	const [showEngagementMapLoadErrorTooolTip, setShowEngagementMapLoadErrorTooolTip] = useState(false);

	const isArchivedEngagement = () => {
		return engagement?.engagementStatusId !== undefined && engagement.engagementStatusId === EngagementStatus.Archived;
	};

	const isEngagementAccessible =
		internalUser?.independenceInformation?.isEngagementAccessible &&
		!user?.isGuestUser &&
		!user?.permissions?.hasIndependenceOnlyRole &&
		!isArchivedEngagement();

	const dispatch = useDispatch();

	const disableEngagementSelector = () => {
		if (engagement && !isProfileComplete) {
			return true;
		} else if (internalUser && internalUser.independenceInformation) {
			/*if looking at my independence show engagement name but not dropdown*/
			return !isEngagementAccessible;
		} else if (user && user.id && !user.isGuestUser) {
			return true;
		} else {
			return true;
		}
	};

	const engagementIsLoading = disableEngagementSelector === true ? false : _.isEmpty(engagement);

	const getCanvasUrl = useCallback(() => {
		if (engagement || engagement.engagementId) {
			return Utility.getCanvasBaseUrl(engagement, user?.displayLanguage);
		}
	}, [engagement, user]);

	const onEngagementMapLoadErrorCallBack = () => {
		setShowEngagementMapLoadErrorTooolTip(true);
		setShowEngagementMap(false);
	};

	const onCloseEngagementMapCallBack = () => {
		setShowEngagementMapLoadErrorTooolTip(false);
		setShowEngagementMap(false);
	};

	const isAIChatBotEnabled = () => {
		return (
			configurations.features?.isOrchestratorEnabled &&
			user != null &&
			!user?.isGuestUser &&
			engagement?.engagementStatusId !== undefined &&
			engagement.engagementStatusId !== EngagementStatus.Archived &&
			!hideAIChatbotForEngagementSources.includes(engagement.engagementSourceId)
		);
	};

	const engagementOptionLabel = ({title, clientname, engdesc, engUrl}) => (
		<a className="AnchorselectOption" href={engUrl}>
			<section title={title} className="selectOption">
				<section className="selectText">{clientname}</section>
				<section className="engName">{engdesc}</section>
			</section>
		</a>
	);

	const engagementSelectorRender = () => {
		if (!engagementMetaData || engagementMetaData.length === 0 || hideEverything) {
			return (
				<section title={engagement ? engagement.engagementDescription : ''} className="EYEngagementDefault">
					{engagement ? engagement.engagementDescription : ''}
				</section>
			);
		} else {
			return (
				<section className={'EYEngagementDropdown ' + (engagementMetaData ? '' : 'hide')}>
					<StyledEngagementSelect
						className="EYEngagementDropdownSelect"
						placeholder="Select Engagement"
						id="RelatedEngagements"
						noOptionsMessage={() => labels.noResultsFoundLabel}
						isClearable={false}
						options={engagementDropdownOptions}
						value={defaultSelectedEngagement}
						defaultValue={defaultSelectedEngagement}
						formatOptionLabel={engagementOptionLabel}
					/>
				</section>
			);
		}
	};

	useEffect(() => {
		if (engagement && engagement.engagementDescription) {
			document.title = `${engagement.engagementDescription}`;
		}

		let iconOptions = [];
		if (apiCallsComplete && engagement?.engagementId && quickLink?.engagementId && configurations?.features && !hideEverything) {
			iconOptions = MenuIconOptions({
				engagement: engagement,
				configurations: configurations,
				formData: formData,
				engagements: engagementMetaData?.engagementmetadatadetails,
				displayLanguage: user?.displayLanguage,
				isAutomationHubEnabled: isAutomationHubEnabled,
				gateWayOptionsOpen: gateWayOptionsOpen,
				setGateWayOptionsOpen: setGateWayOptionsOpen,
				selectedIcon: selectedIcon,
				setSelectedIcon: setSelectedIcon,
				quickLinks: quickLink,
				setShowEngagementMap: setShowEngagementMap,
				showEngagementMapLoadErrorTooolTip: showEngagementMapLoadErrorTooolTip,
				userPreferences: user,
				isClientYearEndDateAfter: isClientYearEndDateAfter
			});
		}

		setIconOptions(iconOptions);
		setShowChatbotIcon(isAIChatBotEnabled());
	}, [engagement, formData, configurations, quickLink, apiCallsComplete, engagementMetaData, selectedIcon, showEngagementMapLoadErrorTooolTip]);

	const teamsAvatarRender = () => {
		return <TeamsInfo personData={personData} personCardInteraction={'none'} theme={mode} key={'userAvatar'} />;
	};

	const handleSettingsClick = () => {
		window.open(zeusUrl + `/usersettings?referrer=${new URL(window.location.href)}`, '_self');
	};

	const notificationRender = () => {
		return (
			<>
				{configurations && configurations.features && configurations.features.enableNotifications ? (
					<div className="notifications-icon-wrapper">
						<RemoteComponent
							key="notifications"
							id="notifications"
							adConfig={envConfig.ad}
							engagementId={engagement.engagementId}
							darkHeader
							showDummyTriggerButton={false}
							displayLanguage={user?.displayLanguage}
							darkMode={mode}
							newBaseFontSize={true}
							numberFormat={user?.numberFormat}
							liveNotificationDelayInSeconds={configurations.features?.liveNotificationDelayInSeconds}
							loadingFallback={<NotificationPlaceholder notificationMessage={labels.notificationUpbutSomeLoadingErrorMessage} mode={mode} />}
							errorFallback={<NotificationPlaceholder notificationMessage={labels.notificationDownErrorMessage} mode={mode} />}
						/>
						<NotificationPlaceholder notificationMessage={labels.notificationUpbutSomeLoadingErrorMessage} mode={mode} />
					</div>
				) : (
					<></>
				)}
			</>
		);
	};

	const getBookMarks = () => {
		if (engagement && engagement.engagementId) {
			dispatch(
				getBookmarkAction(
					env.getBaseURL(serviceURL, engagement.engagementId) + 'Tasks?bookmark=true',
					env.getBaseURL(serviceURL, engagement.engagementId),
					engagement.workspaceId
				)
			);
		}
	};

	const handleApiError = (result, errorMessage) => {
		if (result?.response?.status === 403) {
			dispatch(raiseJSValidationError(null, errorMessage, false, false));
		}
	};

	const getEngagementQuickLinks = async () => {
		try {
			!quickLink && (await dispatch(getQuickLinks(engagementId)));
		} catch (result) {
			handleApiError(result, labels.invalidEngagementId);
		}
	};

	const getConfigurationFeatures = async () => {
		if (configurations && configurations.features) return;

		const configurationUrl = env.getBaseURL(serviceURL) + 'FeatureConfigurations';
		try {
			await dispatch(getConfigurations(configurationUrl));
		} catch (error) {
			// Handle any potential errors here if needed
			console.error('Error fetching configuration features:', error);
		}
	};

	useEffect(() => {
		let result = [];
		if (engagementMetaData && engagementMetaData?.engagementmetadatadetails?.length > 0 && !defaultSelectedEngagement) {
			engagementMetaData.engagementmetadatadetails.map((eng) => {
				const status = eng.engagementstatusid,
					indstatus = eng.independencestatusid;
				if (
					status === EngagementStatus.Active ||
					status === EngagementStatus.Restored ||
					status === EngagementStatus.MarkedForDeletion ||
					status === EngagementStatus.CanvasExternalAccessPortalV2
				) {
					if (
						indstatus === independenceStatus.NOMATTERSIDENTIFIED ||
						indstatus === independenceStatus.MATTERRESOLVEDGRANTEDACCESS ||
						indstatus === independenceStatus.NOTAPPLICABLE
					) {
						const displayedEngagement = {
							value: eng.engagementid,
							label: eng.engagementdescription + ' ' + eng.clientname,
							title: eng.engagementdescription + ' ' + eng.clientname,
							clientname: eng.clientname,
							engdesc: eng.engagementdescription,
							engUrl: (dashboardDomain === undefined ? window.location.origin : dashboardDomain) + '?engagementid=' + eng.engagementid
						};
						if (engagement?.engagementId === Number(eng.engagementid)) {
							setDefaultSelectedEngagement([displayedEngagement]);
						} else {
							result.push(displayedEngagement);
						}
					}
				}
			});
			setEngagementDropdownOptions(result);
		}
	}, [engagementMetaData]);

	const initializeEngagementData = async () => {
		if (!isEngagementAccessible || apiCallsComplete) return;

		const promiseArray = buildPromiseArray();

		await Promise.allSettled(promiseArray).then(() => {
			setApiCallsComplete(true);
		});
	};

	const buildPromiseArray = () => {
		//const promises = [getEngagementInformation()];
		// this is done in main.js. No need for it here.
		let promises = [];
		if (shouldFetchQuickLinks()) {
			promises.push(getEngagementQuickLinks());
		}

		if (shouldFetchConfigurations()) {
			promises.push(getConfigurationFeatures());
		}

		return promises;
	};

	const shouldFetchQuickLinks = () => quickLink && quickLink.length === 0;

	const shouldFetchConfigurations = () => !configurations.features;

	useEffect(() => {
		initializeEngagementData();
	}, [internalUser, engagement]);

	useEffect(() => {
		if (formData && formData.id && isEngagementAccessible) {
			if (
				formData.documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL ||
				formData.documentTypeId === documentTypes.PROFILEV2 ||
				(formData.documentTypeId === documentTypes.MULTI_ENTITY_INDIVIDUAL_PROFILE_V2_NO_SIGN_OFF_REQUIRED && engagement?.isProfileComplete === false)
			) {
				setHideEverything(true);
			} else {
				setHideEverything(false);
			}
		}
	}, [formData]);

	useEffect(() => {
		if (user) {
			setPersonData({
				person: {
					firstName: user.firstName,
					lastName: user.lastName,
					emailAddress: user.emailAddress,
					viewType: 'avatar'
				}
			});
		}
	}, [user]);

	return (
		<StyledMotifNavWrapper className="StyledMotifNavWrapper motif-no-padding">
			<MotifNav
				key="motifNav"
				applicationName={labels.canvas}
				applicationNameRedirectUrl={zeusUrl}
				disableLogo={!isEngagementAccessible}
				loading={engagementIsLoading}
				showIconLinks={!hideEverything}
				topNavLinks={iconOptions}
				copyrightMessage={labels.copyrightMessage}
				showMoreMenuOptions={!hideEverything}
				showSelectorDropdown
				selectorDropdown={engagementSelectorRender}
				showNotifications={!hideEverything && configurations && configurations.features && configurations.features.enableNotifications}
				notifications={!hideEverything && notificationRender}
				showSettings={false}
				showSearchBox={false}
				settingsOnClick={handleSettingsClick}
				showAvatar
				teamsAvatar={teamsAvatarRender}
				showAIChatbot={showChatbotIcon}
				isAIChatbotActive={showAIChatbot}
				aiChatbotOnclick={() => {
					setShowAIChatbot(!showAIChatbot);
				}}
				chatbotTitle={labels.aiChatbot}
			>
				<>
					{!hideEverything && (
						<MotifMenuOptions
							key={'motifMenuOptions'}
							quickLinks={quickLink}
							baseCanvasUrl={getCanvasUrl()}
							isOfflineMode={offlinePrep === true}
							getBookmarkAction={getBookMarks}
							engagementId={engagement.engagementId}
							engagementDescription={engagement.engagementDescription}
							gaRoleTypeId={engagement.gaRoleTypeId}
							featureConfigurations={configurations}
							engagement={engagement}
						/>
					)}
				</>
			</MotifNav>
			{showAIChatbot && (
				<StyledChatBoxWrapper className={'chatbot-wrapper'}>
					<AIChatbot setShow={setShowAIChatbot} show={showAIChatbot} />
				</StyledChatBoxWrapper>
			)}
			{showEngagementMap && (
				<EngagementMap showEngagementMap={showEngagementMap} onClose={onCloseEngagementMapCallBack} onLoadError={onEngagementMapLoadErrorCallBack} />
			)}
		</StyledMotifNavWrapper>
	);
}

TopBarNav.displayName = 'TopBarNav';

const StyledMotifNavWrapper = styled.section`
	.motif-dropdown {
		&.GateWayOptionsDropdown {
			.motif-dropdown-menu {
				gap: var(--spacing-small);
				.ChildSection {
					&:hover {
						background-color: var(--neutrals-50);
						cursor: pointer;
					}
					a {
						color: var(--neutrals-900);
						font-size: var(--px-16);
						font-weight: 300;
						line-height: var(--px-24);
						width: 100%;
						padding: var(--spacing-medium-small) var(--spacing-medium-large);
						&:hover,
						&:focus,
						&:active,
						& {
							color: var(--neutrals-900);
							text-decoration: none;
							border: 0;
						}
						.motif-icon {
							display: inline-flex;
							svg {
								width: 1.2rem;
								height: 1.2rem;
								margin-top: 0 !important;
							}
						}
						.SpanLableTxt {
							display: inline-flex;
							align-items: center;
							font-size: var(--px-14);
							font-weight: 300;
							color: var(--neutrals-900);
						}
					}
				}
			}
		}
	}
`;

const StyledChatBoxWrapper = styled.section``;

const StyledEngagementSelect = styled(ReactSelect)`
	z-index: 999;
`;
